"use client"

import { Fragment, Suspense, useEffect, useState } from "react"

import { useGetCustomer } from "@unlikelystudio/react-ecommerce-hooks"

import type { ExplicitAny } from "~/@types/generics"
import { getLang } from "~/lib/i18n/utils/get-i18n"
import { STORE_DOMAIN } from "~/lib/shopify/constants"
import { formatCheckoutItems } from "~/lib/shopify/utils/format-checkout-items"
import { getReferenceIdFromGID } from "~/lib/shopify/utils/id"
import { useAddToCart } from "~/hooks/useAddToCart"
import { useAlgoliaCartTrackingAttributes } from "~/hooks/useAlgoliaCartTrackingAttributes"
import { useGetStockByMarket } from "~/hooks/useGetStockByMarket"
import useLocale from "~/hooks/useLocale"
import { AlgoliaConversionTracker } from "~/components/ui/AddToCartCta/AlgoliaConversionTracker"
import type { ProductHeaderProps } from "~/components/ui/ProductHeader"
import type { TProduct, TVariant } from "~/components/ui/ProductHeader/_data/types"
import { getProductTrackingDataWithCollections } from "~/components/shared/AnalyticsEvents/utils/get-product-tracking-data-with-collections"
import { variantToTrackingData } from "~/components/shared/AnalyticsEvents/utils/variant-to-tracking-data"
import { useVariantSwitch } from "~/app/[locale]/products/[slug]/(hooks)/useVariantSwitch"

type KleepSize = {
  label: string
  variantId: string
  quantity: boolean
}

declare global {
  interface Window {
    kleep: ExplicitAny
  }
}

export function KleepWidget({
  id,
  variants,
  trackingData,
  product,
}: {
  id: TProduct["id"]
  variants: TVariant[]
  trackingData: ProductHeaderProps["trackingData"]
  product: ProductHeaderProps["productByHandle"]
}) {
  const locale = useLocale()
  const lang = getLang(locale)
  const { data: customer, isInitialLoading: isLoadingCustomer } = useGetCustomer()
  const { activeVariant, onChange } = useVariantSwitch({ variants, defaultVariant: null })
  const { mutate: addCartLines } = useAddToCart(
    [{ id: activeVariant?.id, price: activeVariant?.price, sku: activeVariant?.sku, vpPrice: activeVariant?.vpPrice }],
    (_, variables) => {
      const addedLineItemId = variables?.[0]?.merchandiseId

      if (addedLineItemId) {
        updateAndCheckAlgoliaAttributes({ variantId: addedLineItemId, trackAsAlgoliaConversion, direction: "add" })
      }
    },
    true,
    true
  )
  const { updateAndCheckAlgoliaAttributes } = useAlgoliaCartTrackingAttributes()
  const [trackAsAlgoliaConversion, setTrackAsAlgoliaConversion] = useState(false)

  const skus = variants?.map((variant) => variant.sku).filter(Boolean) ?? []
  const { data, isLoading } = useGetStockByMarket(skus, { enabled: true })

  // Format the variants to the Kleep format
  const variantsSizesWithQuantity = variants.reduce<KleepSize[]>((acc, variant, index) => {
    acc.push({
      label: variant.size ?? "",
      variantId: String(getReferenceIdFromGID(variant.id) ?? 0),
      quantity: Boolean((data?.[index] || 0) > 0),
    })
    return acc
  }, [])

  useEffect(() => {
    if (isLoading) return
    const interval = setInterval(async () => {
      if (isLoadingCustomer) return
      if (window?.kleep) {
        const kleep = window.kleep

        await kleep.load({
          domain: STORE_DOMAIN,
          product_id: getReferenceIdFromGID(id),
          lang,
          selectors: {
            sizing: "#kleep-container",
          },
          getSizes: async () => {
            return variantsSizesWithQuantity
          },
          addToCart: async (variantId: number) => {
            const activeVariant = variants.find((variant) => getReferenceIdFromGID(variant.id) === Number(variantId))

            const processedTrackingData =
              trackingData && activeVariant
                ? {
                    ...getProductTrackingDataWithCollections(trackingData, product, locale),
                    ...variantToTrackingData(activeVariant, customer),
                  }
                : null

            const checkoutItems = formatCheckoutItems({
              variant: activeVariant,
              trackingData: processedTrackingData,
            })
            addCartLines(checkoutItems)
          },
          selectSize: async (variantId: number) => {
            onChange(variants.findIndex((variant) => getReferenceIdFromGID(variant.id) === Number(variantId)))
          },
        })

        kleep?.track("product_viewed", {
          productId: getReferenceIdFromGID(id),
        })

        clearInterval(interval)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [isLoading, isLoadingCustomer])

  return (
    <Fragment>
      <Suspense>
        <AlgoliaConversionTracker
          algoliaConversationState={trackAsAlgoliaConversion}
          setAlgoliaConversationState={setTrackAsAlgoliaConversion}
        />
      </Suspense>
    </Fragment>
  )
}
